import { Column, ColumnConfig } from '@ant-design/charts';
import { useLazyQuery } from '@apollo/client';
import { GET_DASHBOARD_IDEAS_STRATEGIC_DRIVERS } from '@frontend/data-access';
import { Card } from 'antd';
import { useEffect, useMemo } from 'react';
import useTranslation from 'next-translate/useTranslation';

/* eslint-disable-next-line */
export interface StrategicDriversProps {
  channelId?: string;
  challengeId?: string;
  innovationSpaceId?: string;
}

export function StrategicDrivers({
  channelId,
  challengeId,
  innovationSpaceId,
}: StrategicDriversProps) {
  const { t } = useTranslation('common');

  const [getData, { data, loading }] = useLazyQuery<{
    dashboardIdeaStrategicDrivers: {
      name: string;
      count: number;
    }[];
  }>(GET_DASHBOARD_IDEAS_STRATEGIC_DRIVERS);

  const chartData = useMemo(() => {
    const realData = data?.dashboardIdeaStrategicDrivers ?? [];
    if (realData.length > 0) {
      return realData;
    }
    // Return placeholder data with 0 count when no data is available
    return [{ name: t('no_data'), count: 0 }];
  }, [data, t]);

  const config: ColumnConfig = {
    data: chartData,
    loading: loading,
    xField: 'name',
    yField: 'count',
    legend: false,
    scale: {
      color: {
        range: ['#529FF0'],
      },
    },
    paddingTop: 20,
    label: {
      text: 'count',
      position: 'top',
      textAlign: 'center',
      dy: -15,
      style: {
        fontFamily: 'Lufga',
      },
    },
    // Ensure y-axis starts from 0 and includes at least value 1
    yAxis: {
      min: 0,
      max: chartData.some((item) => item.count > 0) ? undefined : 1,
    },
  };

  useEffect(() => {
    getData({
      variables: {
        channelId,
        challengeId,
        spaceId: innovationSpaceId,
      },
    });
  }, [challengeId, channelId, getData, innovationSpaceId]);

  return (
    <Card style={{ borderRadius: 20 }} className="space-y-4">
      <div className="text-sm text-gray-900 font-medium">
        {t('strategic_driver')}
      </div>
      <Column {...config} />
    </Card>
  );
}

export default StrategicDrivers;
